import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  MapPin,
} from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  createSimulation,
  fetchSettings,
  geocodeAddress,
  listBrands,
  listContractedPowers,
  type BrandRow,
  type ContractedPowerRow,
  type OdooLead,
  type PhaseType,
  type SettingsRow,
  type TariffType,
} from "@/lib/api"
import { ConsumptionCard } from "@/components/ConsumptionCard"
import { OdooLeadSearch, clientFromOdooLead } from "@/components/OdooLeadSearch"
import { LeadOriginField } from "@/components/LeadOriginField"
import { isLeadOriginComplete, leadOriginPayload } from "@/lib/lead-origin"
import {
  averageMonths,
  estimateMonthlyKwh,
  periodPrices,
  periodTotal,
  toDistribution,
  type ConsumptionMonth,
  type PeriodKwh,
} from "@/lib/consumption"
import { cn } from "@/lib/utils"
import { formatEur } from "@/lib/format"
import { DraggableSatelliteMap } from "@/components/DraggableSatelliteMap"

interface WizardProps {
  shell: AppShell | null
}

interface FormState {
  // Step 1 — BIGINT da DB pode exceder 2^53, manter como string|number
  lead_id: number | string | null
  /** Lead do CRM do Odoo escolhida na pesquisa (D1). */
  odoo_lead_id: number | null
  odoo_lead_name: string | null
  /** Origem da lead (obrigatória). */
  lead_origin: string | null
  lead_origin_detail: string
  client_name: string
  client_email: string
  client_phone: string
  // Step 2
  address: string
  city: string
  postal_code: string
  latitude: string
  longitude: string
  // Step 3
  house_type: "moradia" | "apartamento" | "comercio"
  roof_material: "telha" | "chapa" | "laje" | "telha_marselha"
  roof_inclination_type: "plano" | "inclinado"
  // Step 4
  tariff_type: TariffType
  phase_type: PhaseType
  brand_id: number | null
  contracted_power_id: string
  has_charger: boolean
  has_meter: boolean
  has_battery: boolean
  has_backup: boolean
  has_invoice: boolean
  average_monthly_bill_eur: string
}

const DEFAULT_FORM: FormState = {
  lead_id: null,
  odoo_lead_id: null,
  odoo_lead_name: null,
  lead_origin: null,
  lead_origin_detail: "",
  client_name: "",
  client_email: "",
  client_phone: "",
  address: "",
  city: "",
  postal_code: "",
  latitude: "",
  longitude: "",
  house_type: "moradia",
  roof_material: "telha",
  roof_inclination_type: "inclinado",
  tariff_type: "simples",
  phase_type: "monofasico",
  brand_id: null,
  contracted_power_id: "",
  has_charger: false,
  has_meter: true,
  has_battery: false,
  has_backup: false,
  has_invoice: false,
  average_monthly_bill_eur: "100",
}

const STEPS = [
  { id: 1, label: "Cliente" },
  { id: 2, label: "Localização" },
  { id: 3, label: "Imóvel" },
  { id: 4, label: "Energia" },
  { id: 5, label: "Resultado" },
]

export function Wizard({ shell }: WizardProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [brands, setBrands] = useState<BrandRow[]>([])
  const [powers, setPowers] = useState<ContractedPowerRow[]>([])
  const [creating, setCreating] = useState(false)
  // ── Consumos (aba Energia) — mesmo card do Editar ──────────────────────
  const [settingsRow, setSettingsRow] = useState<SettingsRow | null>(null)
  const [dist, setDist] = useState<PeriodKwh>({ empty: 0.3, peak: 0.4, full: 0.3 })
  const [months, setMonths] = useState<ConsumptionMonth[]>([])

  useEffect(() => {
    if (!shell) return
    Promise.all([
      listBrands(shell),
      listContractedPowers(shell),
      fetchSettings(shell),
    ])
      .then(([brandRows, powerRows, settings]) => {
        const activeBrands = brandRows.filter((b) => b.is_active)
        setBrands(activeBrands)
        setPowers(powerRows.filter((p) => p.is_active))
        setSettingsRow(settings)
        setDist({
          empty: Number(settings.default_consumption_empty_fraction ?? 0.3),
          peak: Number(settings.default_consumption_peak_fraction ?? 0.4),
          full: Number(settings.default_consumption_full_fraction ?? 0.3),
        })
        setForm((f) => ({
          ...f,
          brand_id: settings.default_brand_id ?? activeBrands[0]?.brand_id ?? null,
          contracted_power_id:
            powerRows.find((p) => Number(p.value_kva) === 6.9)?.id ??
            powerRows[0]?.id ??
            "",
          average_monthly_bill_eur: String(settings.auto_create_default_bill_eur ?? 100),
        }))
      })
      .catch((err) => {
        console.error("[wizard] preload failed", err)
        shell.bridge.toast({
          variant: "error",
          description: "Falha a carregar marcas/potências",
        })
      })
  }, [shell])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function canAdvance(): boolean {
    if (step === 1) return form.client_name.trim().length > 0 && isLeadOriginComplete(form)
    if (step === 2) {
      const lat = Number(form.latitude)
      const lng = Number(form.longitude)
      return Number.isFinite(lat) && Number.isFinite(lng)
    }
    if (step === 3) return true
    if (step === 4) {
      // Consumo: basta UM dos dois — valor médio da fatura OU ≥3 meses reais
      // (quando "tem fatura"). Antes exigia sempre o valor da fatura, mesmo
      // com os dados mensais preenchidos (feedback do cliente).
      const hasBill = Number(form.average_monthly_bill_eur) > 0
      const hasMonthsData =
        form.has_invoice &&
        months.filter(
          (m) => Number(m.empty) + Number(m.peak) + Number(m.full) > 0,
        ).length >= 3
      return (
        form.brand_id != null &&
        form.contracted_power_id !== "" &&
        (hasBill || hasMonthsData)
      )
    }
    return true
  }

  async function submit() {
    if (!shell || !form.brand_id) return

    // Consumos: deriva total mensal + distribuição do card (igual ao Editar).
    let perPeriod: PeriodKwh
    let monthsPayload: ConsumptionMonth[] | null = null
    if (form.has_invoice) {
      const filled = months.filter(
        (m) => Number(m.empty) + Number(m.peak) + Number(m.full) > 0,
      )
      if (filled.length < 3) {
        shell.bridge.toast({
          variant: "error",
          description: "Com fatura: preenche pelo menos 3 meses de consumo",
        })
        return
      }
      perPeriod = averageMonths(months)
      monthsPayload = months
    } else {
      const prices = settingsRow
        ? periodPrices(settingsRow, form.tariff_type)
        : { empty: 0, peak: 0, full: 0 }
      const fixedFrac = Number(settingsRow?.estimate_fixed_cost_fraction ?? 0.26)
      perPeriod = estimateMonthlyKwh(
        Number(form.average_monthly_bill_eur) || 0,
        fixedFrac,
        dist,
        prices,
      )
    }
    const monthlyTotalKwh = periodTotal(perPeriod)
    if (monthlyTotalKwh <= 0) {
      shell.bridge.toast({
        variant: "error",
        description: "Consumo inválido — verifica o valor da conta ou os meses",
      })
      return
    }
    const distOut = toDistribution(perPeriod)

    setCreating(true)
    try {
      const res = await createSimulation(shell, {
        lead_id: form.lead_id,
        odoo_lead_id: form.odoo_lead_id,
        odoo_lead_name: form.odoo_lead_name,
        ...leadOriginPayload(form),
        client_name: form.client_name,
        client_email: form.client_email || null,
        client_phone: form.client_phone || null,
        address: form.address || null,
        city: form.city || null,
        postal_code: form.postal_code || null,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        house_type: form.house_type,
        roof_material: form.roof_material,
        roof_inclination_type: form.roof_inclination_type,
        tariff_type: form.tariff_type,
        phase_type: form.phase_type,
        brand_id: form.brand_id,
        contracted_power_id: form.contracted_power_id,
        has_invoice: form.has_invoice,
        average_monthly_bill_eur: Number(form.average_monthly_bill_eur),
        monthly_consumption_total_kwh: monthlyTotalKwh,
        consumption_distribution_empty: distOut.empty,
        consumption_distribution_peak: distOut.peak,
        consumption_distribution_full: distOut.full,
        consumption_months_json: monthsPayload,
        has_charger: form.has_charger,
        has_meter: form.has_meter,
        has_battery: form.has_battery,
        has_backup: form.has_backup,
      })
      shell.bridge.toast({
        variant: "success",
        description: `Simulação ${res.number} criada`,
      })
      navigate(`/solar/${res.id}`)
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description:
          err instanceof Error ? err.message : "Falha a criar simulação",
      })
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Criar Simulação</h1>

      {/* Stepper */}
      <div className="flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => s.id < step && setStep(s.id)}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                step === s.id
                  ? "bg-accent text-accent-foreground"
                  : step > s.id
                    ? "bg-foreground text-background"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {step > s.id ? <Check className="h-3.5 w-3.5" /> : s.id}
            </button>
            <span
              className={cn(
                "text-sm",
                step === s.id ? "font-medium" : "text-muted-foreground",
              )}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="h-px w-6 bg-border" />
            )}
          </div>
        ))}
      </div>

      {step === 1 && <Step1Client shell={shell} form={form} update={update} />}
      {step === 2 && <Step2Location shell={shell} form={form} update={update} />}
      {step === 3 && <Step3Housing form={form} update={update} />}
      {step === 4 && (
        <Step4Energy
          form={form}
          update={update}
          brands={brands}
          powers={powers}
          settingsRow={settingsRow}
          dist={dist}
          setDist={setDist}
          months={months}
          setMonths={setMonths}
        />
      )}
      {step === 5 && (
        <Step5Result form={form} brands={brands} powers={powers} />
      )}

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1 || creating}
        >
          <ArrowLeft className="h-4 w-4" />
          Anterior
        </Button>
        {step < 5 ? (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canAdvance()}
          >
            Seguinte
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={submit} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                A criar simulação…
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Criar Simulação
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

// ============================================================
// Step 1 — Cliente / Lead
// ============================================================

function Step1Client({
  shell,
  form,
  update,
}: {
  shell: AppShell | null
  form: FormState
  update: <K extends keyof FormState>(k: K, v: FormState[K]) => void
}) {
  function selectLead(lead: OdooLead) {
    const c = clientFromOdooLead(lead)
    update("odoo_lead_id", lead.id)
    update("odoo_lead_name", lead.name)
    update("client_name", c.client_name)
    update("client_email", c.client_email)
    update("client_phone", c.client_phone)
    update("address", c.address)
    update("city", c.city)
    update("postal_code", c.postal_code)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cliente</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <OdooLeadSearch
          shell={shell}
          module="solar"
          linked={form.odoo_lead_id ? { id: form.odoo_lead_id, name: form.odoo_lead_name } : null}
          onSelect={selectLead}
          onClear={() => {
            update("odoo_lead_id", null)
            update("odoo_lead_name", null)
          }}
        />

        <LeadOriginField
          value={{ lead_origin: form.lead_origin, lead_origin_detail: form.lead_origin_detail }}
          onChange={(o) => {
            update("lead_origin", o.lead_origin)
            update("lead_origin_detail", o.lead_origin_detail)
          }}
        />

        <Separator />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label required>Nome</Label>
            <Input
              value={form.client_name}
              onChange={(e) => update("client_name", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.client_email}
              onChange={(e) => update("client_email", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Telefone</Label>
            <Input
              value={form.client_phone}
              onChange={(e) => update("client_phone", e.target.value)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================
// Step 2 — Localização
// ============================================================

function Step2Location({
  shell,
  form,
  update,
}: {
  shell: AppShell | null
  form: FormState
  update: <K extends keyof FormState>(k: K, v: FormState[K]) => void
}) {
  const [geocoding, setGeocoding] = useState(false)

  async function runGeocode() {
    if (!shell) return
    const addr = [form.address, form.city, form.postal_code, "Portugal"]
      .filter(Boolean)
      .join(", ")
    if (!addr) {
      shell.bridge.toast({ variant: "error", description: "Indica uma morada" })
      return
    }
    setGeocoding(true)
    try {
      const res = await geocodeAddress(shell, addr)
      update("latitude", res.latitude.toFixed(6))
      update("longitude", res.longitude.toFixed(6))
      shell.bridge.toast({
        variant: "success",
        description: "Localização geocodificada",
      })
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a geocodificar",
      })
    } finally {
      setGeocoding(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Localização</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1 md:col-span-2">
            <Label required>Morada</Label>
            <Input
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Cidade</Label>
            <Input
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Código postal</Label>
            <Input
              value={form.postal_code}
              onChange={(e) => update("postal_code", e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button
            onClick={runGeocode}
            disabled={geocoding}
            variant="secondary"
          >
            {geocoding ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4" />
            )}
            Geocodificar morada
          </Button>
          <span className="text-xs text-muted-foreground">
            Usa a Google Maps API configurada no tenant.
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label required>Latitude</Label>
            <Input
              type="number"
              step="0.000001"
              value={form.latitude}
              onChange={(e) => update("latitude", e.target.value)}
              placeholder="38.7223"
            />
          </div>
          <div className="space-y-1">
            <Label required>Longitude</Label>
            <Input
              type="number"
              step="0.000001"
              value={form.longitude}
              onChange={(e) => update("longitude", e.target.value)}
              placeholder="-9.1393"
            />
          </div>
        </div>

        {form.latitude && form.longitude && (
          <DraggableSatelliteMap
            shell={shell}
            latitude={Number(form.latitude)}
            longitude={Number(form.longitude)}
            onChange={(lat, lon) => {
              update("latitude", lat.toFixed(6))
              update("longitude", lon.toFixed(6))
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================
// Step 3 — Imóvel
// ============================================================

function Step3Housing({
  form,
  update,
}: {
  form: FormState
  update: <K extends keyof FormState>(k: K, v: FormState[K]) => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Imóvel</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label required>Tipo de casa</Label>
            <Select
              value={form.house_type}
              onValueChange={(v) =>
                update("house_type", v as FormState["house_type"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="moradia">Moradia</SelectItem>
                <SelectItem value="apartamento">Apartamento</SelectItem>
                <SelectItem value="comercio">Comércio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label required>Material do telhado</Label>
            <Select
              value={form.roof_material}
              onValueChange={(v) =>
                update("roof_material", v as FormState["roof_material"])
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="telha">Telha</SelectItem>
                <SelectItem value="telha_marselha">Telha marselha</SelectItem>
                <SelectItem value="chapa">Chapa</SelectItem>
                <SelectItem value="laje">Laje</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label required>Inclinação do telhado</Label>
            <Select
              value={form.roof_inclination_type}
              onValueChange={(v) =>
                update(
                  "roof_inclination_type",
                  v as FormState["roof_inclination_type"],
                )
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="plano">Plano (estrutura triangular)</SelectItem>
                <SelectItem value="inclinado">Inclinado (estrutura coplanar)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================
// Step 4 — Energia e consumo
// ============================================================

function Step4Energy({
  form,
  update,
  brands,
  powers,
  settingsRow,
  dist,
  setDist,
  months,
  setMonths,
}: {
  form: FormState
  update: <K extends keyof FormState>(k: K, v: FormState[K]) => void
  brands: BrandRow[]
  powers: ContractedPowerRow[]
  settingsRow: SettingsRow | null
  dist: PeriodKwh
  setDist: (v: PeriodKwh) => void
  months: ConsumptionMonth[]
  setMonths: (v: ConsumptionMonth[]) => void
}) {
  return (
    <div className="space-y-4">
      <ConsumptionCard
        settings={settingsRow}
        tariffType={form.tariff_type}
        onTariff={(v) => update("tariff_type", v)}
        hasInvoice={form.has_invoice}
        onHasInvoice={(v) => update("has_invoice", v)}
        billEur={form.average_monthly_bill_eur}
        onBill={(v) => update("average_monthly_bill_eur", v)}
        dist={dist}
        onDist={setDist}
        months={months}
        onMonths={setMonths}
      />
      <Card>
        <CardHeader>
          <CardTitle>Sistema</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label required>Fase</Label>
            <Select
              value={form.phase_type}
              onValueChange={(v) => update("phase_type", v as PhaseType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monofasico">Monofásico</SelectItem>
                <SelectItem value="trifasico">Trifásico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label required>Marca do Kit</Label>
            <Select
              value={form.brand_id != null ? String(form.brand_id) : ""}
              onValueChange={(v) => update("brand_id", Number(v))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecionar marca…" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((b) => (
                  <SelectItem key={b.brand_id} value={String(b.brand_id)}>
                    {b.brand_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label required>Potência contratada</Label>
            <Select
              value={form.contracted_power_id}
              onValueChange={(v) => update("contracted_power_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecionar…" />
              </SelectTrigger>
              <SelectContent>
                {powers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <SwitchRow
            label="Incluir carregador"
            checked={form.has_charger}
            onChange={(v) => update("has_charger", v)}
          />
          <SwitchRow
            label="Incluir contador"
            checked={form.has_meter}
            onChange={(v) => update("has_meter", v)}
          />
          <SwitchRow
            label="Incluir bateria"
            checked={form.has_battery || form.has_backup}
            disabled={form.has_backup}
            hint={
              form.has_backup
                ? "Incluída automaticamente com o backup"
                : "Armazenamento / auto-consumo"
            }
            onChange={(v) => update("has_battery", v)}
          />
          <SwitchRow
            label="Incluir backup"
            checked={form.has_backup}
            hint="Corte de rede — requer bateria"
            onChange={(v) => {
              update("has_backup", v)
              // Backup implica bateria: liga a bateria automaticamente.
              if (v) update("has_battery", true)
            }}
          />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function SwitchRow({
  label,
  checked,
  onChange,
  disabled = false,
  hint,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  hint?: string
}) {
  return (
    <label className="flex items-center justify-between rounded-md border border-border p-3">
      <span className="flex flex-col">
        <span className="text-sm font-medium">{label}</span>
        {hint ? (
          <span className="text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </label>
  )
}

// ============================================================
// Step 5 — Resultado / Confirmação
// ============================================================

function Step5Result({
  form,
  brands,
  powers,
}: {
  form: FormState
  brands: BrandRow[]
  powers: ContractedPowerRow[]
}) {
  const brand = brands.find((b) => b.brand_id === form.brand_id)
  const power = powers.find((p) => p.id === form.contracted_power_id)
  const estimatedAnnual = useMemo(() => {
    // estimativa simples sem PVGIS (apenas para feedback visual)
    const bill = Number(form.average_monthly_bill_eur)
    if (!Number.isFinite(bill)) return null
    return bill * 12
  }, [form.average_monthly_bill_eur])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md bg-muted p-4 text-sm">
          <p className="text-muted-foreground">
            Ao confirmar, o motor de cálculo determina o kit recomendado,
            consulta o PVGIS para a tua localização, calcula poupança e payback,
            e cria a simulação no estado <strong>draft</strong>.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Cliente" value={form.client_name} />
          <Field label="Email" value={form.client_email || "—"} />
          <Field
            label="Localização"
            value={`${form.latitude}, ${form.longitude}`}
          />
          <Field
            label="Imóvel"
            value={`${form.house_type} · ${form.roof_material} · ${form.roof_inclination_type}`}
          />
          <Field
            label="Tarifa / Fase"
            value={`${form.tariff_type} · ${form.phase_type}`}
          />
          <Field
            label="Marca de kit"
            value={brand?.brand_name ?? `#${form.brand_id}`}
          />
          <Field
            label="Potência contratada"
            value={power?.name ?? "—"}
          />
          <Field
            label="Fatura média"
            value={formatEur(form.average_monthly_bill_eur)}
          />
          <Field
            label="Consumo anual estimado"
            value={
              estimatedAnnual != null ? formatEur(estimatedAnnual) : "—"
            }
          />
          <Field
            label="Opções"
            value={[
              form.has_charger && "Carregador",
              form.has_meter && "Contador",
              (form.has_battery || form.has_backup) && "Bateria",
              form.has_backup && "Backup",
            ]
              .filter(Boolean)
              .join(" · ") || "Nenhuma"}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )
}
